import { Card, Col, Form, type FormInstance, Input, Row, Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { PhoneField } from '@client/components/PhoneField';
import { UploadFiles } from '@client/components/upload/UploadFiles';
import { useTenant } from '@client/hooks/useTenant';
import {
  isEditMode,
  placeholderField,
  requiredField,
} from '@client/utils/form.util';
import { flexLayout } from '@client/utils/layout.util';

import type { ETier, ITenant } from '@common/types';

interface ITenantKYBProps {
  disabled?: boolean;
  formRef: FormInstance;
  tenant: ITenant | null;
}

type TTierOpts = {
  key: ITenant['kyb']['tier'];
  value: ITenant['kyb']['tier'];
  label: string;
};

export const TenantKYB = (props: ITenantKYBProps) => {
  const { disabled, formRef, tenant } = props;
  const [kybLevel, setKybLevel] = React.useState<TTierOpts['key']>(
    formRef.getFieldValue(['kyb', 'tier']),
  );

  const { getTierLimits } = useTenant();

  const { t } = useTranslation();
  const params = useParams();

  const isEdit = isEditMode(params.tenantId);

  const handleTier = async (tier: TTierOpts['key']) => {
    setKybLevel(tier);

    await getTierLimits(tenant, tier as ETier);
  };

  return (
    <Row gutter={[48, 0]}>
      <Col {...flexLayout.halfColumn}>
        <Form.Item
          name={['kyb', 'tier']}
          label={t('tenant.verification')}
          rules={[requiredField(t('tenant.verification'))]}
        >
          <Select
            disabled={disabled}
            placeholder={placeholderField(t('tenant.verification'))}
            onChange={handleTier}
            options={
              [
                {
                  key: 'Basic',
                  value: 'Basic',
                  label: t('tenant.kyb.basic'),
                },
                {
                  key: 'Standard',
                  value: 'Standard',
                  label: t('tenant.kyb.standard'),
                },
                {
                  key: 'Premium',
                  value: 'Premium',
                  label: t('tenant.kyb.premium'),
                },
              ] as TTierOpts[]
            }
          />
        </Form.Item>
      </Col>
      <Col {...flexLayout.fullColumn}>
        <Card
          variant="outlined"
          style={{
            display: kybLevel === 'Basic' ? 'none' : 'block',
          }}
          styles={{
            body: {
              backgroundColor: 'rgb(250,250,250)',
            },
          }}
        >
          <Row gutter={[24, 0]}>
            {kybLevel === 'Standard' && (
              <Col {...flexLayout.halfColumn}>
                <Form.Item
                  label={t('form.phone')}
                  name={['kyb', 'phone']}
                  rules={[requiredField(t('form.phone'))]}
                >
                  <PhoneField
                    ns={['kyb', 'phone']}
                    extension={true}
                    disabled={disabled}
                    isEdit={isEdit}
                    formRef={formRef}
                  />
                </Form.Item>
              </Col>
            )}

            {kybLevel === 'Premium' && (
              <>
                <Col {...flexLayout.halfColumn}>
                  <Form.Item
                    name={['kyb', 'companyName']}
                    label="Company Name"
                    rules={[
                      { required: true, message: 'Company name is required' },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col {...flexLayout.halfColumn}>
                  <Form.Item
                    name={['kyb', 'taxId']}
                    label="Tax ID/Number"
                    rules={[
                      { required: true, message: 'Tax number is required' },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col {...flexLayout.halfColumn}>
                  <Form.Item
                    name={['kyb', 'address']}
                    label="Legal Address"
                    rules={[{ required: true, message: 'Address is required' }]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
                <Col {...flexLayout.halfColumn}>
                  <Form.Item
                    name={['kyb', 'documents']}
                    label="Verification Documents"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      if (Array.isArray(e)) return e;
                      return e && e.fileList;
                    }}
                  >
                    <UploadFiles />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Card>
      </Col>
    </Row>
  );
};
